import type { AppError } from '@obraplan/core/application/errors/app-error';
import type { Session } from '@obraplan/core/application/session';
import type { AttachmentUsage } from '@obraplan/core/application/use-cases/planning/attachment-use-cases';
import type { Attachment, SupportedMimeType } from '@obraplan/core/domain/attachments/attachment';
import type { AttachmentId } from '@obraplan/core/domain/shared/ids';
import { ok, type Result } from '@obraplan/core/domain/shared/result';
import { RDO_NA_FILA, type Outbox, type OutboxEntry } from '@/app/outbox-types';

/**
 * As fotos da fila do aparelho na galeria (Fase 43, RN-621, RN-622).
 *
 * Sem isto, a foto tirada sem sinal só existia como um "aguardando envio" no cartão: não se via
 * qual era, nem se tirava a errada. Aqui a foto que espera entra na galeria do dono dela, com a
 * marca de que ainda não subiu, e a que vai ser removida já sai — como o cartão mostra o veredito
 * esperado (ADR-195).
 *
 * A foto da fila vira um anexo com id próprio (`FOTO_NA_FILA`), e por isso nenhuma caixa que
 * mostra galeria muda: a miniatura sai do próprio arquivo (`urlDaFotoNaFila`), e o remover dela
 * tira o arquivo da fila em vez de chamar o servidor (`Outbox.dropFile`).
 */

/** O começo do id da foto que ainda está na fila: `aparelho:foto:<operação>#<posição>`. */
export const FOTO_NA_FILA = 'aparelho:foto:';

/** De quem é a galeria: a foto da fila aparece só na do dono dela. */
export type DonoDaFoto =
  | { readonly kind: 'conferencia'; readonly resourceId: string; readonly date: string }
  | { readonly kind: 'nc'; readonly nonConformityId: string }
  | {
      readonly kind: 'equipe';
      /** A medição, quando já tem id no servidor. */
      readonly teamMeasurementId?: string;
      /** A frente da equipe: a foto da medição feita sem sinal é achada por ela. */
      readonly teamId: string;
      readonly activityId: string;
      readonly serviceId: string;
    }
  | { readonly kind: 'rdo'; readonly date: string };

interface FotoNaFila {
  readonly id: string;
  readonly file: File;
  readonly caption?: string;
  readonly createdAt: string;
  readonly projectId: string;
}

function doDono(entry: OutboxEntry, dono: DonoDaFoto): boolean {
  const { operation } = entry;
  switch (dono.kind) {
    case 'conferencia':
      return (
        operation.kind === 'fotos-da-conferencia' &&
        operation.resourceId === dono.resourceId &&
        operation.date === dono.date
      );
    case 'nc':
      return operation.kind === 'fotos-da-nc' && operation.nonConformityId === dono.nonConformityId;
    case 'equipe':
      return (
        operation.kind === 'fotos-da-equipe' &&
        ((operation.teamMeasurementId !== undefined &&
          operation.teamMeasurementId === dono.teamMeasurementId) ||
          (operation.teamId === dono.teamId &&
            operation.activityId === dono.activityId &&
            operation.serviceId === dono.serviceId))
      );
    case 'rdo':
      return (
        operation.kind === 'rdo-fotos' &&
        operation.resourceId === RDO_NA_FILA &&
        operation.date === dono.date
      );
  }
}

/** As fotos que esperam na fila para este dono, na ordem em que foram tiradas. */
function fotosNaFila(entries: readonly OutboxEntry[], dono: DonoDaFoto): FotoNaFila[] {
  return entries
    .filter((entry) => doDono(entry, dono))
    .flatMap((entry) => {
      const { operation } = entry;
      if (!('files' in operation)) return [];
      const caption =
        'caption' in operation && operation.caption.trim() !== ''
          ? operation.caption.trim()
          : undefined;
      return operation.files.map((file, posicao) => ({
        id: `${FOTO_NA_FILA}${entry.id}#${String(posicao)}`,
        file,
        createdAt: entry.createdAt,
        projectId: operation.projectId,
        ...(caption === undefined ? {} : { caption }),
      }));
    });
}

/** A foto da fila como anexo da galeria. Só a galeria a lê: o servidor nunca a vê. */
function comoAnexo(foto: FotoNaFila): Attachment {
  return {
    id: foto.id as AttachmentId,
    projectId: foto.projectId,
    entityType: 'DAILY_INSPECTION',
    entityId: foto.id,
    blobKey: '',
    originalName: foto.file.name,
    mimeType: foto.file.type as SupportedMimeType,
    sizeBytes: foto.file.size,
    compressedSizeBytes: foto.file.size,
    checksum: '',
    uploadedByUserId: '',
    uploadedAt: foto.createdAt,
    ...(foto.caption === undefined ? {} : { caption: foto.caption }),
  } as unknown as Attachment;
}

/** Os anexos que a fila vai tirar do servidor (RN-621). */
function removidasNaFila(entries: readonly OutboxEntry[]): ReadonlySet<string> {
  return new Set(
    entries.flatMap(({ operation }) =>
      operation.kind === 'remover-foto' ? [operation.attachmentId] : [],
    ),
  );
}

/**
 * A galeria com a fila por cima: sem as fotos que vão sair, com as que vão entrar no fim. A
 * contagem acompanha, para o limite de fotos valer também para o que espera.
 */
export function galeriaComAFila(
  lida:
    | {
        readonly attachments: readonly Attachment[];
        readonly usage?: AttachmentUsage | undefined;
      }
    | undefined,
  entries: readonly OutboxEntry[],
  dono: DonoDaFoto,
): { attachments: Attachment[]; usage: AttachmentUsage | undefined } {
  const saem = removidasNaFila(entries);
  const ficam = (lida?.attachments ?? []).filter((attachment) => !saem.has(attachment.id));
  const entram = fotosNaFila(entries, dono).map(comoAnexo);
  const usage = lida?.usage;
  return {
    attachments: [...ficam, ...entram],
    usage:
      usage === undefined
        ? undefined
        : {
            ...usage,
            count: usage.count - ((lida?.attachments.length ?? 0) - ficam.length) + entram.length,
          },
  };
}

/** A foto ainda está no aparelho, e não no servidor. */
export function estaNaFila(attachmentId: string): boolean {
  return attachmentId.startsWith(FOTO_NA_FILA);
}

/** A operação e a posição do arquivo, a partir do id da foto da fila. */
export function posicaoNaFila(
  attachmentId: string,
): { readonly entryId: string; readonly index: number } | undefined {
  if (!estaNaFila(attachmentId)) return undefined;
  const resto = attachmentId.slice(FOTO_NA_FILA.length);
  const corte = resto.lastIndexOf('#');
  if (corte < 0) return undefined;
  const index = Number(resto.slice(corte + 1));
  return Number.isInteger(index) ? { entryId: resto.slice(0, corte), index } : undefined;
}

/**
 * A miniatura da foto que ainda está no aparelho: a URL sai do próprio arquivo, e a galeria a
 * revoga como revoga as outras (ADR-109).
 */
export function urlDaFotoNaFila(
  attachmentId: string,
  entries: readonly OutboxEntry[],
): string | undefined {
  const posicao = posicaoNaFila(attachmentId);
  if (posicao === undefined) return undefined;
  const operation = entries.find((entry) => entry.id === posicao.entryId)?.operation;
  if (operation === undefined || !('files' in operation)) return undefined;
  const file = operation.files[posicao.index];
  // Sem `createObjectURL` (o jsdom dos testes), a miniatura fica sem imagem, com o nome.
  if (file === undefined || typeof URL.createObjectURL !== 'function') return undefined;
  return URL.createObjectURL(file);
}

/**
 * Remove uma foto da galeria, com sinal ou sem (RN-621, RN-622).
 *
 * A que ainda está no aparelho sai da fila, e o servidor nunca a vê. A que já subiu vira uma
 * operação da fila na chave de quem a galeria é (`dia.resourceId`): sobe na hora, ou na volta do
 * sinal, depois do que já esperava ali — a foto do RDO depois da revisão que o reabre.
 *
 * `queued` diz que a remoção ainda vai subir.
 */
export async function removerAFoto(
  outbox: Outbox,
  session: Session,
  attachmentId: string,
  dia: { readonly projectId: string; readonly date: string; readonly resourceId: string },
): Promise<Result<{ readonly queued: boolean }, AppError>> {
  const posicao = posicaoNaFila(attachmentId);
  if (posicao !== undefined) {
    const tirada = await outbox.dropFile(posicao.entryId, posicao.index);
    return tirada.ok ? ok({ queued: false }) : tirada;
  }
  const feita = await outbox.submit({ kind: 'remover-foto', ...dia, attachmentId }, session);
  return feita.ok ? ok({ queued: feita.value.queued }) : feita;
}
